// WHERE AN ADDRESS GOES NEXT, AND A STATE'S NAME — the two small readers `/`, `/not-available` and `/checkout` share
// (WEB-PARITY-2B). Kept out of the route files so each of those exports components only.

import type { ResolvePropertyResponse } from "@contract";
import { stateName } from "@v2-state-code";
import { prePayTemplate, prePayText } from "../components/prePayCopy";

/**
 * WHERE AN ADDRESS GOES ONCE THE SERVER HAS ANSWERED FOR IT — the one writer, read by `/` and `/not-available`.
 *
 * Every answer is the server's decision, read, never a rule of this client's:
 *   - `/not-available` for a state the server NAMED and refused (G-1 `block` with the coming-soon reason) — the app's
 *     "We're still finishing \(state)" sheet, as a page;
 *   - null — STAY ON `/` — when the address itself needs the owner: a state the server could not work out (G-1's
 *     `unknownState`, whose sentence says to add the state), a kind of property it refuses (G-5), or a condominium with no
 *     unit (G-4). The app shows each of these on its entry screen or in a sheet over it;
 *   - `/account`, the terms, otherwise. The staging bypass (G-1 `info`) goes on, as it always did, and says so on
 *     `/checkout`.
 */
export function whereNext(response: ResolvePropertyResponse): "/not-available" | "/account" | null {
  const g1 = response.decisions["G-1"];
  if (g1.ran && g1.outcome === "block") return g1.reason === "unknownState" ? null : "/not-available";
  const g5 = response.decisions["G-5"];
  const g4 = response.decisions["G-4"];
  if ((g5.ran && g5.outcome === "block") || (g4.ran && g4.outcome === "block")) return null;
  return "/account";
}

/** A state's name from the code the wire carries, by the server's own table, or null. */
export function stateNameOf(code: string | null): string | null {
  return code === null ? null : stateName(code.trim().toUpperCase());
}

/** The app's title for the can-raise card, by the card the server sent (CanRaise.swift `message(for:)`) — read by
 *  `/checkout`'s card and, since WEB-PARITY-5, by the Letter step's compliance card (GenerateView.swift:1123-1167). */
export function canRaiseTitle(code: string, state: string | null): string | null {
  if (code === "resolve.g2.card.pa") return prePayText("canRaise.title.pa");
  if (code === "resolve.g2.card.ne") {
    const name = stateNameOf(state);
    return name === null ? null : prePayTemplate("canRaise.title.canRaise", { state: name });
  }
  if (code === "resolve.g2.noCard") return prePayText("canRaise.title.unknown");
  return null;
}
